import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Column, Table } from '@tanstack/react-table';
import React, { useState } from 'react';
import { LuEye, LuEyeOff, LuGripVertical, LuSettings2 } from 'react-icons/lu';

import { getColumnLabel } from '@/cip/utils/tableUtils';

import { Button } from '../ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';

interface SortableItemProps<T> {
    column: Column<T, unknown>;
}

function SortableItem<T>({ column }: SortableItemProps<T>) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: column.id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="border-border mb-1 flex items-center justify-between rounded-md border bg-white px-3 py-2"
        >
            <div className="flex items-center gap-3">
                <button
                    className="cursor-grab touch-none text-gray-400 hover:text-gray-600 active:cursor-grabbing"
                    {...attributes}
                    {...listeners}
                >
                    <LuGripVertical className="h-4 w-4" />
                </button>
                <span className="text-sm">
                    {typeof column.columnDef.header === 'string' ? column.columnDef.header : getColumnLabel(column.id)}
                </span>
            </div>
            <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                onClick={() => column.toggleVisibility()}
                aria-label="toggle column visibility"
            >
                {column.getIsVisible() ? <LuEye className="h-4 w-4" /> : <LuEyeOff className="h-4 w-4 text-red-400" />}
            </Button>
        </div>
    );
}

interface TableSettingsProps<T> {
    table: Table<T>;
    excludeColumns?: string[];
    onReset?: () => void;
    iconOnly?: boolean;
    label?: string;
}

export function TableSettings<T>({
    table,
    excludeColumns = [],
    onReset,
    iconOnly = false,
    label = 'Table Settings',
}: TableSettingsProps<T>) {
    const [open, setOpen] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const columns = table
        .getAllLeafColumns()
        .filter((column) => !column.columnDef.meta?.isDisplayCol)
        .filter((column) => !excludeColumns.includes(column.id));

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = columns.findIndex((col) => col.id === active.id);
            const newIndex = columns.findIndex((col) => col.id === over.id);

            const reordered = arrayMove(columns, oldIndex, newIndex);
            table.setColumnOrder(reordered.map((col) => col.id));
        }
    };

    const handleReset = () => {
        table.resetColumnOrder();
        table.resetColumnVisibility();
        table.resetColumnSizing();
        onReset?.();
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {iconOnly ? (
                    <Button variant="ghost2" size="sm" className="h-9 w-9 p-0">
                        <LuSettings2 className="h-4 w-4" />
                    </Button>
                ) : (
                    <Button variant="outline" size="sm" className="gap-2">
                        <LuSettings2 className="h-4 w-4" />
                        {label}
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>Table settings</DialogTitle>
                </DialogHeader>
                <ScrollArea className="max-h-[60vh]">
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={columns.map((col) => col.id)}>
                            <div className="space-y-1">
                                {columns.map((column) => (
                                    <SortableItem key={column.id} column={column} />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                </ScrollArea>
                <DialogFooter>
                    <Button variant="outline" onClick={handleReset}>
                        Reset
                    </Button>
                    <DialogClose asChild>
                        <Button>Close</Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
